// Utility functions for avatar generation and handling

// Predefined gradient backgrounds for contacts without avatars
const gradientBackgrounds: [string, string, ...string[]][] = [
  ['#FF6B6B', '#4ECDC4'],
  ['#45B7D1', '#96CEB4'],
  ['#FECA57', '#FF9FF3'],
  ['#54A0FF', '#5F27CD'],
  ['#00D2D3', '#54A0FF'],
  ['#FF9F43', '#EE5A24'],
  ['#0ABDE3', '#006BA6'],
  ['#C44569', '#F8B500'],
  ['#6C5CE7', '#A29BFE'],
  ['#FD79A8', '#FDCB6E'],
  ['#6C5CE7', '#74B9FF'],
  ['#00B894', '#00CEC9'],
  ['#E17055', '#FDCB6E'],
  ['#A29BFE', '#FD79A8'],
  ['#55A3FF', '#1DD1A1'],
]

// Get random gradient background
export const getRandomGradientBackground = (): [
  string,
  string,
  ...string[],
] => {
  const randomIndex = Math.floor(Math.random() * gradientBackgrounds.length)
  return gradientBackgrounds[randomIndex]
}

// Get consistent gradient background based on string (like user ID or name)
export const getConsistentGradientBackground = (
  seed: string,
): [string, string, ...string[]] => {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32-bit integer
  }

  const index = Math.abs(hash) % gradientBackgrounds.length
  return gradientBackgrounds[index]
}

// Generate initials from name
export const generateInitials = (
  firstName?: string,
  lastName?: string,
): string => {
  const first = firstName?.charAt(0)?.toUpperCase() || ''
  const last = lastName?.charAt(0)?.toUpperCase() || ''

  if (first && last) {
    return `${first}${last}`
  }

  if (first) {
    return first
  }

  return '?'
}

// Get avatar props for user
export const getAvatarProps = (user: {
  first_name?: string
  last_name?: string
  avatar_path?: string
  avatarBg?: [string, string, ...string[]]
  id?: string
}) => {
  const initials = generateInitials(user.first_name, user.last_name)
  const backgroundGradient =
    user.avatarBg ||
    getConsistentGradientBackground(user.id || user.first_name || 'default')

  return {
    initials,
    backgroundGradient,
    avatarUrl: user.avatar_path,
    hasAvatar: Boolean(user.avatar_path),
  }
}

// Check if URL is valid image URL
export const isValidImageUrl = (url?: string): boolean => {
  if (!url) return false

  try {
    const urlObj = new URL(url)
    return ['http:', 'https:'].includes(urlObj.protocol)
  } catch {
    return false
  }
}

// Get fallback avatar color based on name
export const getFallbackAvatarColor = (name: string): string => {
  const colors = [
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#96CEB4',
    '#FECA57',
    '#FF9FF3',
    '#54A0FF',
    '#5F27CD',
    '#00D2D3',
    '#FF9F43',
  ]

  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }

  const index = Math.abs(hash) % colors.length
  return colors[index]
}
